import { type ReactElement, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProjectSettingsViewModel } from '@/features/projects/hooks/useProjectSettingsViewModel';
import { UnauthorizedScreen } from '@/pages/UnauthorizedScreen';
import { useOrganisationEntitlements } from '@/shared/hooks';
import { useSettingsPageLoadingGate } from '@/shared/hooks/useSettingsPageLoadingGate';
import { PageStateScaffold } from '@/ui/composites/scaffolds';
import { SettingsPageLoadingState } from '@/ui/composites/SettingsPageLoadingState';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { ProjectSettingsContent } from './ProjectSettingsContent';
import type { AuthSession } from '@/auth/types';
import type { ProjectDataWipeSectionProps } from '@/features/projects/components';
import type {
  ProjectApiKeySectionProps,
  ProjectDetailsSectionProps,
} from '@/features/projects/components/ProjectSettingsSections';

type ProjectSettingsPageProps = { session: AuthSession | null };

export const ProjectSettingsPage = ({ session }: ProjectSettingsPageProps): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setOverrideItems, clearOverride } = useBreadcrumbs();
  const {
    projectQuery,
    project,
    updateProject,
    rotateProjectApiKey,
    deleteProject,
    testProjectPipelineWebhook,
    wipeProjectData,
    selectedOrganisationId,
    selectedProjectId,
    canManageProject,
    canManageProjectLoading,
    nameValue,
    setNameValue,
    nameDirty,
    setNameDirty,
    badgeEnabledValue,
    setBadgeEnabledValue,
    badgeCharsValue,
    setBadgeCharsValue,
    badgeColorValue,
    setBadgeColorValue,
    apiKeyValue,
    setApiKeyValue,
    revokeOpen,
    setRevokeOpen,
    mutationError,
    apiKeyError,
    suiteThresholdDrafts,
    setSuiteThresholdDrafts,
    suitePreferredUnitsRef,
    resetSuiteThresholdDrafts,
    suiteThresholdsDirty,
    suiteThresholdsValid,
    testThresholdDrafts,
    setTestThresholdDrafts,
    testPreferredUnitsRef,
    resetTestThresholdDrafts,
    testThresholdsDirty,
    testThresholdsValid,
    executionRunThresholdDrafts,
    setExecutionRunThresholdDrafts,
    executionRunPreferredUnitsRef,
    resetExecutionRunThresholdDrafts,
    executionRunThresholdsDirty,
    executionRunThresholdsValid,
    pipelineThresholdDrafts,
    setPipelineThresholdDrafts,
    pipelinePreferredUnitsRef,
    resetPipelineThresholdDrafts,
    pipelineThresholdsDirty,
    pipelineThresholdsValid,
    stageThresholdDrafts,
    setStageThresholdDrafts,
    stagePreferredUnitsRef,
    resetStageThresholdDrafts,
    stageThresholdsDirty,
    stageThresholdsValid,
    jobThresholdDrafts,
    setJobThresholdDrafts,
    jobPreferredUnitsRef,
    resetJobThresholdDrafts,
    jobThresholdsDirty,
    jobThresholdsValid,
    notify,
  } = useProjectSettingsViewModel(session);
  const resolvedProject = project ?? null;
  const entitlementsQuery = useOrganisationEntitlements(resolvedProject?.organisationId ?? null);
  const entitlements = entitlementsQuery.data ?? null;
  const hideUploadTokenSettings = false;
  const hidePipelineWebhookSettings =
    entitlements !== null && !entitlements.addOns.operationsEnabled;
  const hidePipelineMetricThresholds =
    entitlements !== null &&
    entitlements.plan.code === 'trial' &&
    !entitlements.addOns.operationsEnabled;
  const isProjectSettingsRoute = location.pathname === '/project-settings';
  const isLoading = useSettingsPageLoadingGate(
    `${location.pathname}${location.search}`,
    !projectQuery.isFetching && !canManageProjectLoading,
  );

  useEffect(() => {
    if (isProjectSettingsRoute) return;
    clearOverride();
  }, [clearOverride, isProjectSettingsRoute]);

  if (!isProjectSettingsRoute) {
    return <></>;
  }

  useEffect(() => {
    if (!resolvedProject) return;
    setOverrideItems([
      { label: 'Projects', href: '/projects' },
      { label: resolvedProject.name, href: `/projects/${resolvedProject.slug}` },
      { label: 'Settings' },
    ]);
  }, [resolvedProject, setOverrideItems]);

  useEffect(
    () => () => {
      clearOverride();
    },
    [clearOverride],
  );

  useEffect(() => {
    const handleOrgRemoved = (): void => {
      void navigate('/projects', {
        replace: true,
        state: { notice: 'You no longer have access to that workspace' },
      });
    };

    window.addEventListener('flowqa-org-removed', handleOrgRemoved);
    return () => {
      window.removeEventListener('flowqa-org-removed', handleOrgRemoved);
    };
  }, [navigate]);

  const projectDetailsSection: ProjectDetailsSectionProps = {
    project: resolvedProject,
    nameValue,
    setNameValue,
    nameDirty,
    setNameDirty,
    badgeEnabledValue,
    setBadgeEnabledValue,
    badgeCharsValue,
    setBadgeCharsValue,
    badgeColorValue,
    setBadgeColorValue,
    updateProject,
    mutationError,
    notify,
  };

  const projectApiKeySection: ProjectApiKeySectionProps = {
    project: resolvedProject,
    apiKeyValue,
    setApiKeyValue,
    revokeOpen,
    setRevokeOpen,
    updateProject,
    rotateProjectApiKey,
    testProjectPipelineWebhook,
    apiKeyError,
    notify,
  };

  const projectDataWipeSection: ProjectDataWipeSectionProps = {
    project: resolvedProject,
    deleteProject,
    wipeProjectData,
    mutationError,
    notify,
    onProjectDeleted: () => {
      void navigate('/projects', {
        replace: true,
        state: { notice: 'Project deleted' },
      });
    },
  };

  const viewModel = {
    projectQuery,
    project,
    updateProject,
    mutationError,
    selectedOrganisationId,
    selectedProjectId,
    canManageProject,
    canManageProjectLoading,
    suiteThresholdDrafts,
    setSuiteThresholdDrafts,
    suitePreferredUnitsRef,
    resetSuiteThresholdDrafts,
    suiteThresholdsDirty,
    suiteThresholdsValid,
    testThresholdDrafts,
    setTestThresholdDrafts,
    testPreferredUnitsRef,
    resetTestThresholdDrafts,
    testThresholdsDirty,
    testThresholdsValid,
    executionRunThresholdDrafts,
    setExecutionRunThresholdDrafts,
    executionRunPreferredUnitsRef,
    resetExecutionRunThresholdDrafts,
    executionRunThresholdsDirty,
    executionRunThresholdsValid,
    pipelineThresholdDrafts,
    setPipelineThresholdDrafts,
    pipelinePreferredUnitsRef,
    resetPipelineThresholdDrafts,
    pipelineThresholdsDirty,
    pipelineThresholdsValid,
    stageThresholdDrafts,
    setStageThresholdDrafts,
    stagePreferredUnitsRef,
    resetStageThresholdDrafts,
    stageThresholdsDirty,
    stageThresholdsValid,
    jobThresholdDrafts,
    setJobThresholdDrafts,
    jobPreferredUnitsRef,
    resetJobThresholdDrafts,
    jobThresholdsDirty,
    jobThresholdsValid,
    notify,
    navigate,
  };
  return (
    <PageStateScaffold
      session={session}
      sessionMessage="Sign in to manage project settings."
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      requireProject
      projectMessage="Select a project to manage settings."
      requireAdmin
      isAdmin={canManageProject}
      adminFallback={
        <UnauthorizedScreen
          title="Unauthorised"
          body="You must be an admin to manage project settings."
          backTo="/projects"
          backLabel="Back to projects"
        />
      }
      redirecting={projectQuery.isFetched && resolvedProject === null}
      redirectMessage="Redirecting to projects..."
      isLoading={isLoading}
      loadingFallback={<SettingsPageLoadingState message="Loading project settings..." />}
      isError={Boolean(projectQuery.error)}
      error={projectQuery.error}
    >
      <ProjectSettingsContent
        viewModel={viewModel}
        projectDetailsSection={projectDetailsSection}
        projectApiKeySection={projectApiKeySection}
        projectDataWipeSection={projectDataWipeSection}
        hideUploadTokenSettings={hideUploadTokenSettings}
        hidePipelineWebhookSettings={hidePipelineWebhookSettings}
        hidePipelineMetricThresholds={hidePipelineMetricThresholds}
      />
    </PageStateScaffold>
  );
};
